<template>
  <div class="schedule-fleet-task mc-button-dropdown">
    <b-dropdown
      id="schedule-fleet-task"
      toggle-tag="div"
      toggle-class="p-0 bg-transparent border-0"
    >
      <template #button-content>
        <McButton
          variant="secondary"
          icon="icon-outline_chevron-down"
          icon-position="right"
          :text="$t('delivery.action.scheduleFleetTask')"
        />
      </template>

      <b-dropdown-item @click="openDirectModal">
        {{ $t('delivery.status.direct') }}
        <i
          id="tooltip-direct"
          class="mc-fa icon-outline_info"
          style="position: relative; bottom: -2px; font-size: 16px"
        >
        </i>
        <b-tooltip target="tooltip-direct" triggers="hover" placement="rightbottom">
          {{ $t('delivery.message.shipmentDirectToFinalDestination') }}
        </b-tooltip>
      </b-dropdown-item>

      <b-dropdown-item @click="openTransitModal">
        {{ $t('delivery.status.transit') }}
        <i
          id="tooltip-transit"
          class="mc-fa icon-outline_info"
          style="position: relative; bottom: -2px; font-size: 16px"
        >
        </i>
        <b-tooltip target="tooltip-transit" triggers="hover" placement="rightbottom">
          {{ $t('delivery.message.multipleFleetTasksWithTransit') }}
        </b-tooltip>
      </b-dropdown-item>
    </b-dropdown>

    <b-modal
      id="modal-schedule-fleet-task"
      class="schedule-fleet-task__modal"
      :title="$t('delivery.action.scheduleFleetTask')"
      size="md"
      centered
      scrollable
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
    >
      <div v-if="statusDoTransit" class="card-body p-2">
        <McAlert
          class="mb-2"
          variant="info"
          :text="$t('delivery.message.deliveryCurrentlyInTransit')"
          withIcon
          :withGap="false"
        />
        <p class="schedule-fleet-task__text">
          {{ `${$t('delivery.fields.fleetTaskNumber')}:` }}
          <strong class="text-secondary">{{ lastFleetTask?.fleet_task?.number }}</strong>
        </p>
        <p class="schedule-fleet-task__text">
          {{ `${$t('delivery.fields.transitLocation')}:` }}
          <strong class="text-secondary">{{ lastFleetTask?.destination_address?.name }}</strong>
        </p>
      </div>
      <div v-if="hasConfirmedMixedProductCategory">
        <McAlert withIcon class="w-100 mb-3" variant="warning">
          <template #label>
            <span><b>{{ $t('global.alert.text.warningUpperCase') + '!' }}</b> {{ $t('delivery.message.mixedProductCategoryOrEmptyDelivery') }}</span>
          </template>
        </McAlert>
      </div>
      <div class="card-body p-2">
        <p class="schedule-fleet-task__text">
          {{ $t('delivery.message.scheduleFleetTaskForDelivery') }}
          <strong>{{ showResponse?.number }}</strong
          >?
        </p>
        <p class="schedule-fleet-task__text" v-html="scheduledStatusFleetTaskCreatedMessage"></p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.action.no')"
            :disabled="isLoading"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="$t('global.action.yes')"
            :disabled="isLoading"
            @click="handleSubmit"
          />
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-schedule-fleet-task-transit"
      class="schedule-fleet-task__modal"
      :title="$t('delivery.action.scheduleFleetTasktTransit')"
      size="md"
      centered
      scrollable
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
    >
      <div v-if="statusDoTransit" class="card-body p-2">
        <McAlert
          class="mb-2"
          variant="info"
          :text="$t('delivery.message.deliveryCurrentlyInTransit')"
          withIcon
          :withGap="false"
        />
        <p class="schedule-fleet-task__text">
          {{ `${$t('delivery.fields.fleetTaskNumber')}:` }}
          <strong class="text-secondary">{{ lastFleetTask?.fleet_task?.number }}</strong>
        </p>
        <p class="schedule-fleet-task__text">
          {{ `${$t('delivery.fields.transitLocation')}:` }}
          <strong>{{ lastFleetTask?.destination_address?.name }}</strong>
        </p>
      </div>
      <div v-if="hasConfirmedMixedProductCategory">
        <McAlert withIcon class="w-100 mb-3" variant="warning">
          <template #label>
            <span><b>{{ $t('global.alert.text.warningUpperCase') + '!' }}</b> {{ $t('delivery.message.mixedProductCategoryOrEmptyDelivery') }}</span>
          </template>
        </McAlert>
      </div>
      <div class="card-body p-2">
        <FormulateForm ref="formRef" v-model="formValues" class="mc-form mb-2" :errors="formErrors">
          <FormulateInput
            :ref="`${transitLocationFields.name}-ref`"
            v-bind="{ ...transitLocationFields }"
            :fullObject.sync="transitAddressFullObject"
            class="p-0"
            validation-behavior="submit"
            :help="transitLocationFields?.validation?.includes('required')"
          />
        </FormulateForm>

        <p class="schedule-fleet-task__text">
          {{ $t('delivery.message.scheduleFleetTaskForDelivery') }}
          <strong>{{ showResponse?.number }}</strong>
          {{ '?' }}
        </p>
        <p class="schedule-fleet-task__text" v-html="scheduledStatusFleetTaskCreatedMessage"></p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.action.cancel')"
            :disabled="isLoading"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="$t('delivery.action.scheduleFleetTask')"
            :disabled="isLoading"
            @click="handleSubmitTransit"
          />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import * as FleetTask from '@/services/rest-api/lib/fleet-task/fleet-task'
import * as Address from '@/services/rest-api/lib/delivery/address'
import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import * as Load from '@/services/rest-api/lib/delivery/load'
import { errorCodeToastMap } from '@/router/views/fleet-task/lib/constants/error-code-mappings'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McAlert,
  },
  props: {
    showResponse: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isLoading: false,
      formValues: {},
      formErrors: {},
      transitAddressFullObject: {},
      hasConfirmedMixedProductCategory: false,
    }
  },
  computed: {
    scheduledStatusFleetTaskCreatedMessage() {
      return this.$t('delivery.message.deliveryStatusScheduledAndFleetTaskCreated', {
        status: `<strong>${this.$t('delivery.status.scheduled')}</strong>`,
      })
    },
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    transitLocationFields() {
      const transitField = {
        name: 'transit_address_id',
        showKey: 'origin_address',
        type: 'select-resourceful-v2',
        label: this.statusDoTransit
          ? this.$t('delivery.fields.nextTransitLocation')
          : this.$t('delivery.fields.transitLocation'),
        optionsGetter: Address.getAddressTransit,
        optionLabelFn: ({ item }) => item.name,
        placeholder: this.statusDoTransit
          ? this.$t('delivery.placeholder.nextTransitLocation')
          : this.$t('delivery.action.selectTransitLocation'),
        validation: 'required',
      }
      return transitField
    },
    statusDoTransit() {
      return (
        this.showResponse?.shipment_type === 'TRANSIT' &&
        ['Sedang Transit', 'Dijadwalkan', 'Dijalankan', 'Diambil'].includes(
          this.showResponse?.status?.name
        )
      )
    },
    lastFleetTask() {
      return this.showResponse.transits[this.showResponse.transits.length - 1]
    },
    hasSingleProductCategoryAccess() {
      return this.hasAccessAll(['fleet-task-single-category-deliveries:read'])
    },
  },
  watch: {
    'formValues.transit_address_id': {
      handler(newValue) {
        if (newValue) {
          this.formErrors = {}
        }
      },
    },
  },
  methods: {
    async openDirectModal() {
      this.$bvModal.show('modal-schedule-fleet-task')
      this.hasConfirmedMixedProductCategory = await this.checkMixProductCategory()
    },
    async openTransitModal() {
      this.$bvModal.show('modal-schedule-fleet-task-transit')
      this.hasConfirmedMixedProductCategory = await this.checkMixProductCategory()
    },
    async handleSubmit() {
      if (this.isLoading) return

      this.isLoading = true
      let fleetTaskId = null
      const hasConfirmedMixedProductCategory = await this.checkMixProductCategory()
      this.hasConfirmedMixedProductCategory = hasConfirmedMixedProductCategory

      const payload = {
        delivery_order_ids: [this.showResponse.id],
      }
      if (
        this.hasSingleProductCategoryAccess &&
        hasConfirmedMixedProductCategory === false
      ) {
        const productCategoryId = await this.getProductCategoryIdFromFirstLoad()
        if (productCategoryId) {
          payload.product_category_id = productCategoryId
        }
      }

      const res = await FleetTask.store(payload)
      if (res.errors) {
        this.$mcToast.error(this.$t('delivery.alert.failedToCreateFleetTask'))
        this.isLoading = false
        this.$bvModal.hide('modal-schedule-fleet-task')

        return
      }

      fleetTaskId = res.data.id
      this.$store.dispatch('fleetTask/setSelectedDos', [this.showResponse.id])
      this.isLoading = false

      this.$router.push({
        name: 'Fleet Task Detail',
        params: { id: fleetTaskId },
        query: { isSetNavsource: false },
      })
    },
    async handleSubmitTransit() {
      if (this.isLoading) return

      this.isLoading = true
      let fleetTaskId = null

      if (!this.formValues.transit_address_id) {
        this.formErrors = {
          transit_address_id: [this.$t('delivery.validation.transitLocationRequired')],
        }
        return
      }

      const res = await FleetTask.store({
        transit_delivery_orders: [
          {
            delivery_order_ids: [this.showResponse.id],
            transit_address_id: this.formValues.transit_address_id,
          },
        ],
      })
      let toastMessage = this.$t('delivery.alert.failedToCreateFleetTask')
      if (res.errors) {
        const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
        if (issue) toastMessage = errorCodeToastMap[issue.code]
        this.$mcToast.error(toastMessage)
        this.isLoading = false
        this.$bvModal.hide('modal-schedule-fleet-task-transit')
        return
      }

      fleetTaskId = res.data.id
      this.$store.dispatch('fleetTask/setSelectedDos', [this.showResponse.id])
      this.isLoading = false

      this.$router.push({
        name: 'Fleet Task Detail',
        params: { id: fleetTaskId },
        query: { isSetNavsource: false },
      })
    },
    async checkMixProductCategory() {
      if (!this.hasSingleProductCategoryAccess) return false
      const res = await Delivery.validateDo({
        validation_type: 'HAS_MIXED_PRODUCT_CATEGORY',
        ids: [this.showResponse.id],
      })

      return res?.result
    },
    async getProductCategoryIdFromFirstLoad() {
      const getLoads = Load.get(this.showResponse.id)
      const res = await getLoads({
        limit: 1,
        page: 1,
      })

      if (res?.errors) return null

      const firstLoad = res?.data?.paginated_result?.[0]

      return (
        firstLoad?.product?.product_type?.product_category?.id ||
        firstLoad?.product_type?.product_category?.id ||
        null
      )
    },
  },
}
</script>
